import React, { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  AlertTriangle, 
  CheckCircle,
  BarChart3,
  PieChart,
  Activity,
  Calendar,
  Maximize2,
  Download
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { KPIEditor } from './KPIEditor';
import type { FinancialAnalysis, FinancialKPIs, MonthlyData } from '../../types/businessPlan';

interface FinancialDashboardProps {
  analysis: FinancialAnalysis;
  onKPIUpdate?: (updatedKPIs: FinancialKPIs) => void;
  className?: string;
  isSimulation?: boolean;
  comparisonData?: FinancialAnalysis[];
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export const FinancialDashboard: React.FC<FinancialDashboardProps> = ({
  analysis,
  onKPIUpdate,
  className = '',
  isSimulation = false,
  comparisonData = []
}) => {
  const [activeChart, setActiveChart] = useState<'revenue' | 'cashflow' | 'margins' | 'breakdown'>('revenue');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentKPIs, setCurrentKPIs] = useState<FinancialKPIs>(analysis.kpis);

  const { monthly_data, risk_assessment } = analysis;
  const kpis = currentKPIs;

  const handleKPIUpdate = (updatedKPIs: FinancialKPIs) => {
    setCurrentKPIs(updatedKPIs);
    if (onKPIUpdate) {
      onKPIUpdate(updatedKPIs);
    }
  };

  const getRiskColor = (indicator: string) => {
    switch (indicator) {
      case 'green': return 'text-green-600 bg-green-100 border-green-200';
      case 'orange': return 'text-orange-600 bg-orange-100 border-orange-200';
      case 'red': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getRiskIcon = (indicator: string) => {
    switch (indicator) {
      case 'green': return <CheckCircle className="w-6 h-6" />;
      case 'orange': return <AlertTriangle className="w-6 h-6" />;
      case 'red': return <AlertTriangle className="w-6 h-6" />;
      default: return <Activity className="w-6 h-6" />;
    }
  };

  const getRiskLabel = (indicator: string) => {
    switch (indicator) {
      case 'green': return 'Risque Faible';
      case 'orange': return 'Risque Modéré';
      case 'red': return 'Risque Élevé';
      default: return 'Non Évalué';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const exportDashboard = () => {
    // TODO: Implement PDF export
    console.log('Export dashboard as PDF');
  };

  const pieData = [
    { name: 'Charges Fixes', value: monthly_data[0]?.charges_fixes || 0, color: '#ef4444' },
    { name: 'Charges Variables', value: monthly_data[0]?.charges_variables || 0, color: '#f59e0b' },
    { name: 'Marge Nette', value: monthly_data[0]?.marge_nette || 0, color: '#10b981' }
  ];

  return (
    <div className={`space-y-6 ${className} ${isFullscreen ? 'fixed inset-4 z-50 bg-white p-6 overflow-auto rounded-xl shadow-2xl' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {isSimulation ? '🧪 Simulation Financière' : '📊 Analyse Financière'}
          </h2>
          <p className="text-gray-600 mt-1">
            Indicateurs clés et projections de votre business plan
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={exportDashboard}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Exporter PDF</span>
          </button>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Mode plein écran"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* KPI Editor */}
      <KPIEditor
        kpis={kpis}
        onUpdate={handleKPIUpdate}
        isLoading={false}
      />

      {/* Chart Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Visualisations</h3>
          <div className="flex items-center space-x-2">
            {[
              { key: 'revenue', label: 'Revenus', icon: DollarSign },
              { key: 'cashflow', label: 'Trésorerie', icon: Activity },
              { key: 'margins', label: 'Marges', icon: TrendingUp },
              { key: 'breakdown', label: 'Répartition', icon: PieChart }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveChart(key as any)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeChart === key 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Charts */}
        <div className="h-80">
          {activeChart === 'revenue' && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthly_data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}K€`} />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), 'CA']}
                  labelFormatter={(label) => `Mois: ${label}`}
                />
                <Area 
                  type="monotone" 
                  dataKey="ca" 
                  stroke="#3b82f6" 
                  fill="#3b82f6" 
                  fillOpacity={0.3}
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}

          {activeChart === 'cashflow' && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthly_data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}K€`} />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), '']}
                />
                <Line 
                  type="monotone" 
                  dataKey="cashflow" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  name="Cashflow Mensuel"
                />
                <Line 
                  type="monotone" 
                  dataKey="cashflow_cumule" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  name="Cashflow Cumulé"
                />
              </LineChart>
            </ResponsiveContainer>
          )}

          {activeChart === 'margins' && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly_data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `${value.toFixed(0)}%`} />
                <Tooltip 
                  formatter={(value: number) => [`${value.toFixed(1)}%`, '']}
                />
                <Bar dataKey="marge_brute" fill="#10b981" name="Marge Brute" />
                <Bar dataKey="marge_nette" fill="#3b82f6" name="Marge Nette" />
              </BarChart>
            </ResponsiveContainer>
          )}

          {activeChart === 'breakdown' && (
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </RechartsPieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Risk Indicator */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Évaluation des Risques</h3>
          <div className="text-center">
            <div className={`w-20 h-20 rounded-full border-4 flex items-center justify-center mx-auto mb-4 ${getRiskColor(kpis.risk_indicator)}`}>
              {getRiskIcon(kpis.risk_indicator)}
            </div>
            <p className={`text-xl font-bold mb-2 ${getRiskColor(kpis.risk_indicator).split(' ')[0]}`}>
              {getRiskLabel(kpis.risk_indicator)}
            </p>
            <p className="text-sm text-gray-600">
              Score : {risk_assessment.score}/100
            </p>
          </div>
        </div>

        {/* Risk Factors */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Facteurs de Risque</h3>
          <div className="space-y-3">
            {risk_assessment.factors.slice(0, 4).map((factor, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{factor.name}</p>
                  <p className="text-sm text-gray-600">{factor.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    factor.impact === 'high' ? 'bg-red-100 text-red-800' :
                    factor.impact === 'medium' ? 'bg-orange-100 text-orange-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {factor.impact === 'high' ? 'Impact Élevé' :
                     factor.impact === 'medium' ? 'Impact Moyen' : 'Impact Faible'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Summary and Recommendations */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">🤖 Analyse IA et Recommandations</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Summary */}
          <div>
            <h4 className="font-medium text-blue-900 mb-2">Résumé de l'Analyse</h4>
            <p className="text-blue-800 text-sm leading-relaxed">
              {analysis.summary}
            </p>
          </div>

          {/* Recommendations */}
          <div>
            <h4 className="font-medium text-blue-900 mb-2">Recommandations</h4>
            <ul className="space-y-2">
              {analysis.recommendations.slice(0, 3).map((recommendation, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-sm text-blue-800">{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Comparison View (if comparison data exists) */}
      {comparisonData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Comparaison de Scénarios</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}K€`} />
                <Tooltip />
                <Line 
                  data={monthly_data}
                  type="monotone" 
                  dataKey="ca" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  name="Scénario Actuel"
                />
                {comparisonData.map((comparison, index) => (
                  <Line 
                    key={index}
                    data={comparison.monthly_data}
                    type="monotone" 
                    dataKey="ca" 
                    stroke={COLORS[index + 1]} 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name={`Comparaison ${index + 1}`}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};